/**
 * 조직(OrgUnit) 데이터 모델 타입 정의
 *
 * 이 파일은 Firestore의 orgUnits 컬렉션에 저장되는 조직 정보를 TypeScript로 정의합니다.
 *
 * 설계 원칙:
 * 1. 단일 컬렉션에 모든 조직 단위(본부/실/팀) 저장
 * 2. parentId로 계층 구조 표현
 * 3. path 필드로 하위 조직 조회 최적화
 * 4. 파견 조직 등 향후 확장 가능한 구조
 */

import { Timestamp } from 'firebase/firestore';

// ============================================
// 조직 타입 관련
// ============================================

/**
 * 조직 단위 타입
 *
 * - company: 회사 (최상위 회사 조직, 예: NGLE)
 * - headquarter: 본부 (회사 아래)
 * - division: 실 (본부 아래)
 * - team: 팀 (실 아래)
 * - other: 기타 조직 (프로젝트 팀, TF 등)
 */
export type OrgUnitType = 'company' | 'headquarter' | 'division' | 'team' | 'other';

/**
 * 조직 레벨
 *
 * 계층 구조에서의 깊이를 나타냅니다.
 * - 0: 본부 (headquarter)
 * - 1: 실 (division)
 * - 2: 팀 (team)
 * - 3+: 기타 하위 조직
 */
export type OrgUnitLevel = 0 | 1 | 2 | 3;

/**
 * 조직 상태
 *
 * - active: 정상 운영 중
 * - inactive: 비활성 (일시 중단)
 * - archived: 해체됨 (과거 이력 보관용)
 */
export type OrgUnitStatus = 'active' | 'inactive' | 'archived';

// ============================================
// 조직(OrgUnit) 메인 타입
// ============================================

/**
 * 조직(OrgUnit) 정보
 *
 * Firestore의 orgUnits 컬렉션에 저장되는 문서 구조입니다.
 *
 * 설계 철학:
 * 1. 단일 컬렉션 + parentId 방식으로 계층 구조 표현
 * 2. path 필드로 "하위 조직 전체 조회" 최적화
 * 3. level 필드로 계층 깊이 빠르게 파악
 * 4. 확장 필드로 파견 조직, 특수 조직 대응
 *
 * 예시 계층:
 * - 플랫폼본부 (headquarter, level=0, parentId=null)
 *   - 개발1실 (division, level=1, parentId="플랫폼본부")
 *     - A팀 (team, level=2, parentId="개발1실")
 *     - B팀 (team, level=2, parentId="개발1실")
 */
export interface OrgUnit {
  // ============================================
  // 기본 정보
  // ============================================

  /**
   * 조직 ID
   * Firestore 문서 ID와 동일
   *
   * 명명 규칙 제안:
   * - "hq_{name}" (본부)
   * - "div_{name}" (실)
   * - "team_{name}" (팀)
   *
   * 예: "hq_platform", "div_dev1", "team_a"
   */
  id: string;

  /**
   * 조직 이름
   *
   * 예: "플랫폼본부", "개발1실", "A팀"
   */
  name: string;

  /**
   * 조직 코드 (선택 사항)
   *
   * 회사에서 사용하는 공식 조직 코드
   * 예: "HQ-PLATFORM", "DIV-DEV1", "TEAM-A"
   *
   * 용도:
   * - 외부 시스템 연동
   * - 회계/비용 코드
   * - 공식 보고서 작성
   */
  code?: string;

  /**
   * V26.1: 회사 코드 (type='company'인 루트 조직에서만 사용)
   *
   * 멀티회사 지원을 위한 회사 식별자
   * - 'KR': 한국 법인 (NGLE 본사)
   * - 'CN': 중국 법인
   *
   * **중요:**
   * - type='company'인 루트 조직에만 설정
   * - 하위 조직은 이 필드를 사용하지 않음
   * - 하위 조직의 회사는 루트까지 타고 올라가서 판정
   *
   * @see functions/src/utils/companyCodeHelper.ts
   */
  companyCode?: 'KR' | 'CN';

  /**
   * 조직 타입
   *
   * headquarter | division | team | other
   */
  type: OrgUnitType;

  /**
   * 계층 레벨
   *
   * - 0: 본부
   * - 1: 실
   * - 2: 팀
   * - 3+: 기타
   *
   * 용도:
   * - 계층 깊이를 빠르게 확인
   * - UI에서 들여쓰기 표시
   * - 권한 체크 (예: 레벨 1 이상만 실장 가능)
   */
  level: OrgUnitLevel;

  /**
   * 조직 상태
   */
  status: OrgUnitStatus;

  // ============================================
  // 계층 구조 관련
  // ============================================

  /**
   * 상위 조직 ID
   *
   * - 최상위 본부: null
   * - 실: 본부 ID
   * - 팀: 실 ID
   *
   * 예:
   * - "플랫폼본부": null
   * - "개발1실": "hq_platform"
   * - "A팀": "div_dev1"
   */
  parentId: string | null;

  /**
   * 조직 경로 (Path)
   *
   * 최상위부터 현재 조직까지의 ID를 슬래시(/)로 연결한 문자열
   *
   * 용도:
   * - 하위 조직 전체 조회 최적화
   * - Firestore 쿼리: where('path', '>=', '/hq_platform/').where('path', '<', '/hq_platform0')
   * - 계층 구조 표시
   *
   * 예:
   * - "플랫폼본부": "/hq_platform"
   * - "개발1실": "/hq_platform/div_dev1"
   * - "A팀": "/hq_platform/div_dev1/team_a"
   *
   * 주의:
   * - 반드시 슬래시(/)로 시작
   * - 마지막에는 슬래시 없음
   * - 조직 이동 시 하위 조직들의 path도 모두 업데이트 필요
   */
  path: string;

  /**
   * 정렬 순서
   *
   * 같은 레벨의 조직들 사이에서 표시 순서
   * 숫자가 작을수록 먼저 표시
   *
   * 예:
   * - 플랫폼본부: 1
   * - 경영지원본부: 2
   * - 영업본부: 3
   *
   * 용도:
   * - 조직도 표시 순서
   * - 드롭다운 메뉴 순서
   */
  order: number;

  // ============================================
  // 조직 책임자 (Chapter 4-3)
  // ============================================

  /**
   * 조직장(조직 책임자) ID
   *
   * - 본부: 본부장
   * - 실: 실장
   * - 팀: 팀장
   *
   * 선택 사항 (조직은 있지만 책임자가 미지정일 수 있음)
   *
   * users 컬렉션의 문서 ID(employeeId)를 참조
   *
   * **Chapter 4-3 조직도 기능:**
   * - 조직도에서 조직장 정보 표시
   * - 한 사람이 여러 조직의 조직장을 겸임할 수 있음
   * - 주 소속 조직이 아닌 경우 "이름 (겸임)" 표시
   */
  leaderId?: string | null;

  /**
   * 조직장 이름 스냅샷
   *
   * 조직장의 이름을 스냅샷으로 저장하여,
   * 조직장이 퇴사하거나 이름이 변경되어도
   * 조직도에서 이력을 확인할 수 있도록 합니다.
   *
   * **사용 시나리오:**
   * - 조직장 지정 시: Employee의 nameKo를 복사하여 저장
   * - 조직도 표시 시: leaderId로 조회 실패하면 이 값 사용
   * - 히스토리 추적: 과거 조직장 정보 보존
   *
   * **Chapter 4-3 조직도 기능:**
   * - 조직장이 퇴사/삭제된 경우에도 이름 표시 가능
   * - 조직 변경 이력 추적 용이
   */
  leaderNameSnapshot?: string;

  // ============================================
  // 조직장 / 승인자 (Phase 4)
  // ============================================

  /**
   * 조직장 직원 ID (Phase 4)
   *
   * - 조직의 관리 책임자
   * - 조직장은 자동 출퇴근 처리 대상
   * - 겸임 조직장도 1개만 출퇴근 기록 생성
   *
   * users 컬렉션의 문서 ID(employeeId)를 참조
   */
  orgManagerEmployeeId?: string;

  /**
   * 승인자 직원 ID (Phase 4)
   *
   * - 조직원의 각종 요청(휴가, 근로일정, 출퇴근수정, 연장근로 등)을 승인하는 사람
   * - 이 필드가 있으면 이 사람이 승인자
   * - 이 필드가 없으면 orgManagerEmployeeId가 승인자 역할 수행
   *
   * **승인자 결정 로직:**
   * ```ts
   * const approverId = orgUnit.approverEmployeeId || orgUnit.orgManagerEmployeeId;
   * ```
   *
   * users 컬렉션의 문서 ID(employeeId)를 참조
   */
  approverEmployeeId?: string;

  /**
   * V154.1: 승인자 이름 (Cloud Function에서 join하여 반환)
   *
   * getOrgUnitsForAdmin에서 approverEmployeeId 기반으로 users 컬렉션에서 조회하여 채움
   * DB에 저장되지 않고 조회 시에만 사용되는 파생 필드
   */
  approverName?: string;

  /**
   * 조직장 User ID (엑셀 매핑용 별칭)
   * @alias orgManagerEmployeeId
   *
   * orgManagerEmployeeId와 동일한 값을 가리킵니다.
   * 엑셀 업로드/매핑 편의를 위한 별칭 필드입니다.
   */
  managerUserId?: string;

  /**
   * 승인자 User ID (엑셀 매핑용 별칭)
   * @alias approverEmployeeId
   *
   * approverEmployeeId와 동일한 값을 가리킵니다.
   * 엑셀 업로드/매핑 편의를 위한 별칭 필드입니다.
   */
  approverUserId?: string;

  /**
   * @deprecated managerId는 leaderId로 대체됩니다 (Chapter 4-3)
   * 하위 호환성을 위해 유지하지만, 새 코드에서는 leaderId 사용을 권장합니다.
   */
  managerId?: string;

  /**
   * @deprecated managerInfo는 leaderNameSnapshot으로 대체됩니다 (Chapter 4-3)
   * 하위 호환성을 위해 유지하지만, 새 코드에서는 leaderNameSnapshot 사용을 권장합니다.
   */
  managerInfo?: {
    name: string;
    email: string;
    syncedAt: Timestamp;
  };

  // ============================================
  // 확장 필드 (파견 조직, 특수 조직 등)
  // ============================================

  /**
   * 외부 조직 여부
   *
   * - true: 파견 조직 (외부 회사)
   * - false or undefined: 내부 조직 (엔글소속)
   *
   * 용도:
   * - 파견직 직원이 속한 조직 구분
   * - 조직도에서 외부 조직 다르게 표시
   */
  isExternal?: boolean;

  /**
   * 외부 회사명
   *
   * isExternal === true인 경우 사용
   *
   * 예: "ABC인력파견", "XYZ아웃소싱"
   *
   * 용도:
   * - 파견 조직 표시: "ABC인력파견 - 개발팀"
   * - 파견직 관리
   */
  externalCompanyName?: string;

  /**
   * 조직 설명
   *
   * 조직의 역할, 담당 업무 등을 간단히 설명
   *
   * 예: "플랫폼 개발 및 인프라 관리", "고객사 파견 인력 관리"
   */
  description?: string;

  /**
   * V75.2: 휴가 알림 DL 이메일 목록
   *
   * 해당 조직 소속 직원의 휴가 최종승인 시 메일을 받을 DL 주소 목록
   * 예: ["team-a-dl@company.com", "manager@company.com"]
   */
  leaveNotificationDlEmails?: string[];

  /**
   * 메타데이터 (확장 가능한 Key-Value)
   *
   * 향후 필요한 추가 정보를 유연하게 저장
   *
   * 예:
   * - location: "서울 강남"
   * - buildingFloor: "7층"
   * - costCenter: "CC-1001"
   * - budgetCode: "BG-DEV-2024"
   */
  metadata?: Record<string, any>;

  // ============================================
  // 시스템 필드
  // ============================================

  /**
   * 생성일
   */
  createdAt: Timestamp;

  /**
   * 수정일
   */
  updatedAt: Timestamp;

  /**
   * 생성자 (HR 또는 관리자 UID)
   */
  createdBy: string;

  /**
   * 수정자 (HR 또는 관리자 UID)
   */
  updatedBy: string;
}

// ============================================
// 조직 관련 헬퍼 타입
// ============================================

/**
 * 조직 생성 입력 데이터
 *
 * 새로운 조직을 생성할 때 사용하는 입력 데이터
 */
export interface CreateOrgUnitInput {
  /**
   * 조직 이름
   */
  name: string;

  /**
   * 조직 코드 (선택)
   */
  code?: string;

  /**
   * 조직 타입
   */
  type: OrgUnitType;

  /**
   * 상위 조직 ID
   * 본부 생성 시: null
   */
  parentId: string | null;

  /**
   * 조직 책임자 ID (선택)
   * @deprecated managerId는 사용되지 않습니다. managerUserId를 사용하세요.
   */
  managerId?: string;

  /**
   * 조직장 직원 ID (선택)
   * users 컬렉션의 문서 ID를 참조
   */
  managerUserId?: string | null;

  /**
   * 승인자 직원 ID (선택)
   * users 컬렉션의 문서 ID를 참조
   * 지정하지 않으면 managerUserId가 승인자 역할 수행
   */
  approverUserId?: string | null;

  /**
   * 정렬 순서
   * 지정하지 않으면 마지막으로 추가
   */
  order?: number;

  /**
   * 외부 조직 여부 (선택)
   */
  isExternal?: boolean;

  /**
   * 외부 회사명 (선택)
   */
  externalCompanyName?: string;

  /**
   * 설명 (선택)
   */
  description?: string;

  /**
   * 생성자 UID
   */
  createdBy: string;
}

/**
 * 조직 수정 입력 데이터
 *
 * 기존 조직 정보를 수정할 때 사용하는 입력 데이터
 */
export interface UpdateOrgUnitInput {
  /**
   * 조직 이름 (선택)
   */
  name?: string;

  /**
   * 조직 코드 (선택)
   */
  code?: string;

  /**
   * 상위 조직 ID (선택)
   * V29.9: 조직 이동 시 사용
   */
  parentId?: string | null;

  /**
   * 조직 타입 (선택)
   * V29.9: 타입 변경 시 사용
   */
  type?: OrgUnitType;

  /**
   * 조직 상태 (선택)
   */
  status?: OrgUnitStatus;

  /**
   * 조직 책임자 ID (선택)
   * @deprecated managerId는 사용되지 않습니다. managerUserId를 사용하세요.
   */
  managerId?: string;

  /**
   * 조직장 직원 ID (선택)
   * users 컬렉션의 문서 ID를 참조
   */
  managerUserId?: string | null;

  /**
   * 승인자 직원 ID (선택)
   * users 컬렉션의 문서 ID를 참조
   * 지정하지 않으면 managerUserId가 승인자 역할 수행
   */
  approverUserId?: string | null;

  /**
   * 정렬 순서 (선택)
   */
  order?: number;

  /**
   * 설명 (선택)
   */
  description?: string;

  /**
   * 외부 조직 여부 (선택)
   * V29.9: 외부 협력사/파트너 조직 표시
   */
  isExternal?: boolean;

  /**
   * 외부 회사명 (선택)
   * V29.9: isExternal=true일 때 사용
   */
  externalCompanyName?: string | null;

  /**
   * V75.2: 휴가 알림 DL 이메일 목록 (선택)
   */
  leaveNotificationDlEmails?: string[];

  /**
   * 수정자 UID (선택 - useOrgUnitMutations에서 자동 설정)
   */
  updatedBy?: string;
}

/**
 * 조직 이동 입력 데이터
 *
 * 조직을 다른 상위 조직으로 이동할 때 사용
 * (조직 개편 시 사용)
 */
export interface MoveOrgUnitInput {
  /**
   * 이동할 조직 ID
   */
  orgUnitId: string;

  /**
   * 새로운 상위 조직 ID
   * null이면 최상위로 이동
   */
  newParentId: string | null;

  /**
   * 처리자 UID
   */
  processedBy: string;

  /**
   * 이동 사유 (선택)
   */
  reason?: string;
}

/**
 * 조직 계층 정보
 *
 * 특정 조직의 전체 계층 경로를 나타냄
 */
export interface OrgUnitHierarchy {
  /**
   * 조직 ID
   */
  id: string;

  /**
   * 조직 이름
   */
  name: string;

  /**
   * 조직 타입
   */
  type: OrgUnitType;

  /**
   * 계층 레벨
   */
  level: OrgUnitLevel;

  /**
   * 상위 조직들 (본부 → 실 → 팀 순서)
   */
  ancestors: Array<{
    id: string;
    name: string;
    type: OrgUnitType;
  }>;

  /**
   * 하위 조직들
   */
  children?: OrgUnitHierarchy[];
}

/**
 * 조직 목록 필터
 *
 * 조직 목록 조회 시 검색/필터링 조건
 */
export interface OrgUnitListFilter {
  /**
   * 조직명 검색
   *
   * 부분 문자열 매칭 (대소문자 구분 없음)
   * 예: "개발" 입력 시 "개발1실", "개발2실", "플랫폼개발팀" 모두 매칭
   */
  name?: string;

  /**
   * 조직 타입 필터
   *
   * 특정 타입의 조직만 조회
   * 예: 'team' → 팀만 조회
   */
  type?: OrgUnitType;

  /**
   * 사용 여부 필터
   *
   * - true: active 상태인 조직만
   * - false: inactive/archived 상태인 조직만
   * - undefined: 전체 조회
   */
  isActive?: boolean;

  /**
   * 상위 조직 ID 필터
   *
   * 특정 조직의 직속 하위 조직만 조회
   * 예: parentId="hq_platform" → 플랫폼본부 산하 실들만 조회
   */
  parentId?: string | null;
}

// ============================================
// Firestore 매핑 함수
// ============================================

/**
 * Firestore 문서를 OrgUnit 객체로 변환
 *
 * Firestore의 orgUnits 컬렉션에서 조회한 문서를
 * TypeScript OrgUnit 인터페이스로 변환합니다.
 *
 * @param doc Firestore QueryDocumentSnapshot
 * @returns OrgUnit 객체
 *
 * 사용 예시:
 * ```ts
 * import { collection, getDocs } from 'firebase/firestore';
 * import { db } from '../firebase/config';
 * import { mapOrgUnitDocToOrgUnit } from '../types/orgUnit';
 *
 * const snapshot = await getDocs(collection(db, 'orgUnits'));
 * const orgUnits = snapshot.docs.map(mapOrgUnitDocToOrgUnit);
 * ```
 *
 * 주의사항:
 * - Firestore 필드명과 TypeScript 인터페이스 필드명이 일치해야 합니다.
 * - 실제 Firestore 스키마와 다를 경우 이 함수를 수정해야 합니다.
 */
export function mapOrgUnitDocToOrgUnit(
  doc: import('firebase/firestore').QueryDocumentSnapshot
): OrgUnit {
  const data = doc.data();

  return {
    // 기본 정보
    id: doc.id,
    name: data.name || '',
    code: data.code,
    companyCode: data.companyCode, // V26.1: 회사 루트에서만 사용
    type: data.type || 'other',
    level: data.level ?? 0,
    status: data.status || 'active',

    // 계층 구조
    parentId: data.parentId ?? null,
    path: typeof data.path === 'string' ? data.path : (Array.isArray(data.path) ? '/' + data.path.join('/') : `/${doc.id}`),
    order: data.order ?? 0,

    // 조직 책임자
    leaderId: data.leaderId,
    leaderNameSnapshot: data.leaderNameSnapshot,
    orgManagerEmployeeId: data.orgManagerEmployeeId || data.managerUserId,
    approverEmployeeId: data.approverEmployeeId || data.approverUserId,
    managerUserId: data.managerUserId || data.orgManagerEmployeeId,
    approverUserId: data.approverUserId || data.approverEmployeeId,
    managerId: data.managerId, // deprecated
    managerInfo: data.managerInfo, // deprecated

    // 확장 필드
    isExternal: data.isExternal ?? false,
    externalCompanyName: data.externalCompanyName,
    description: data.description,
    leaveNotificationDlEmails: data.leaveNotificationDlEmails, // V75.4
    metadata: data.metadata,

    // 시스템 필드
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
    createdBy: data.createdBy || '',
    updatedBy: data.updatedBy || '',
  };
}

/**
 * OrgUnit을 Firestore 저장 형식으로 변환
 *
 * OrgUnit 객체를 Firestore에 저장할 수 있는 형식으로 변환합니다.
 * (id 필드 제외 - Firestore 문서 ID로 사용)
 *
 * @param orgUnit OrgUnit 객체
 * @returns Firestore 저장용 데이터
 *
 * 사용 예시:
 * ```ts
 * import { doc, setDoc } from 'firebase/firestore';
 * import { db } from '../firebase/config';
 * import { mapOrgUnitToFirestore } from '../types/orgUnit';
 *
 * const orgUnitData = mapOrgUnitToFirestore(orgUnit);
 * await setDoc(doc(db, 'orgUnits', orgUnit.id), orgUnitData);
 * ```
 */
export function mapOrgUnitToFirestore(orgUnit: OrgUnit): Omit<OrgUnit, 'id'> {
  const { id, ...data } = orgUnit;
  return data;
}
